import React, { useCallback, useEffect, useState } from 'react';
import { Select, Typography } from 'antd';
import { aiSalesAPI } from '../services/api';

/**
 * Shared <Select> for picking which AlayaEss agent powers an SPO entity.
 *
 * Used by AI Sales bot edit dialog, chatbot config form, scenario site
 * admin, and the centralized /alayaess-bindings page. Each surface stores
 * the chosen `bot_name` in its own table column (alayaess_bot_name).
 *
 * Props:
 *   value      — current bot_name string (or null)
 *   onChange   — (newBotName | null) => void
 *   placeholder — optional override
 *   style      — passthrough to Select
 *   size       — passthrough to Select ("small" | "middle" | "large")
 */
let _cache = null;          // module-level cache shared across instances
let _cacheTs = 0;
const CACHE_TTL_MS = 30_000;

export default function AlayaessAgentPicker({ value, onChange, placeholder, style, size, disabled }) {
  const [state, setState] = useState({ loading: true, configured: null, error: null, items: [] });

  const load = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && _cache && (now - _cacheTs) < CACHE_TTL_MS) {
      setState({ loading: false, ...(_cache) });
      return;
    }
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const res = await aiSalesAPI.listAlayaessAgents();
      const body = res.data ?? res;
      const next = {
        configured: body.configured,
        error: body.ok ? null : (body.error || 'Unable to list agents'),
        items: body.agents || [],
      };
      _cache = next;
      _cacheTs = now;
      setState({ loading: false, ...next });
    } catch (e) {
      setState({ loading: false, configured: null, error: e.message, items: [] });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const placeholderText =
    placeholder
    || (state.loading
        ? '載入中…'
        : state.configured === false
          ? '— 尚未設定 DISPATCH_DIRECTORY_TOKEN —'
          : state.error
            ? `(無法載入: ${state.error})`
            : '選擇 AlayaEss agent（留空 = fallback Mode A）');

  const options = state.items.map(a => ({
    value: a.bot_name,
    label: `${a.display_name || a.bot_name} (${a.bot_name}${a.tenant_id ? ` · ${a.tenant_id}` : ''})`,
  }));

  // If value is set but doesn't appear in options (e.g. agent removed upstream),
  // still render the chip so the user can see the orphan binding.
  if (value && !options.some(o => o.value === value)) {
    options.unshift({ value, label: `${value}（⚠ 找不到對應 agent）` });
  }

  return (
    <Select
      value={value || undefined}
      onChange={(v) => onChange(v || null)}
      placeholder={placeholderText}
      loading={state.loading}
      options={options}
      allowClear
      showSearch
      optionFilterProp="label"
      style={style}
      size={size}
      disabled={disabled || (state.configured === false && options.length === 0)}
    />
  );
}

export function AlayaessAgentPickerHint({ block = true }) {
  const [state, setState] = useState(_cache);
  useEffect(() => {
    let id;
    if (!_cache) {
      id = setInterval(() => {
        if (_cache) { setState(_cache); clearInterval(id); }
      }, 500);
    }
    return () => id && clearInterval(id);
  }, []);
  if (!state) return null;
  if (state.configured === false) {
    return (
      <Typography.Text type="warning" style={{ fontSize: 12, display: block ? 'block' : 'inline' }}>
        後端尚未設定 DISPATCH_DIRECTORY_TOKEN — 所有 bot 會 fallback 到 Mode A（共用 Mika 人設）。請先在 AI 模型設定 → AlayaEss Dispatch 區塊貼上 directory token。
      </Typography.Text>
    );
  }
  if (state.error) {
    return (
      <Typography.Text type="danger" style={{ fontSize: 12, display: block ? 'block' : 'inline' }}>
        無法列出 AlayaEss agents：{state.error}
      </Typography.Text>
    );
  }
  return null;
}
